import { reactive, watch, inject, onBeforeMount } from 'vue'
import { useBranchesStore, useAuthStore } from 'src/stores'
import { generateImageUrls } from 'src/common/helpers/generator/filePath.helper'
import translate from 'src/common/helpers/language/translate'
import translationObject from '../../../pages/Inventory/Item/ItemLibrary/translate/itemLibrary.translate'
import { IBaseItemDialogHookArgs } from './interfaces'
import { IItemView, ISettingContext, ItemViewResponse } from 'src/common/interfaces'
import { useAxios } from 'src/services/__vue_query__/api/fetchByApiClient'
import { Endpoints } from 'src/services/__vue_query__/endpoints'
import { handleAxiosResponse } from 'src/services/__vue_query__/api/helpers'
import useNotify from 'src/hook/useNotify'
import { catchError } from 'src/common/helpers/generator/error.helper'

export default function useBaseItemDialogHook({ props, model }: IBaseItemDialogHookArgs) {
  /* -------------------------- Translation Variables ------------------------- */

  const { t } = translate(translationObject)

  /* -------------------------------- Constants ------------------------------- */

  const ctx = inject<ISettingContext>('ctx')
  const currency_code = ctx?.currency_code ?? 'gbp'
  const { notify } = useNotify()

  const branchesStore = useBranchesStore()
  const authStore = useAuthStore()
  const store_id =
    typeof branchesStore.selected_store_ids === 'number'
      ? branchesStore.selected_store_ids
      : branchesStore.selected_store_ids[0]
  const business_id = authStore.portalUser.business_id

  const state = reactive({
    item: {} as IItemView,
    // loading state
    loading: { fetch: false },
  })

  /* -------------------------------- Functions ------------------------------- */

  /**
   * Fetches the item data and entity resource data from the server and generates the url path for images
   */
  async function getData() {
    if (props.id) {
      try {
        state.loading.fetch = true
        const filter = { id: props.id, store_id }
        const request = useAxios<ItemViewResponse>(Endpoints.INVENTORY_ITEM_VIEW, { filter })
        await request.fetch()
        const { data } = handleAxiosResponse<ItemViewResponse>(request)
        state.item = data.list[0]
        state.item.images = generateImageUrls(
          { ...state.item, images: state.item.images.map((image) => Number(image)) },
          'item',
          String(business_id),
        )
      } catch (err) {
        const errors = catchError(err)
        for (const error of errors) notify(false, error)
      } finally {
        state.loading.fetch = false
      }
    }
  }

  /* -------------------------------- Watchers -------------------------------- */

  onBeforeMount(async () => await getData())

  watch(
    () => props.id,
    async () => {
      if (model.value) await getData()
    },
  )

  return { state, currency_code, t }
}
